import { useState, useEffect } from 'react';
import { BillingService, type UserSubscription } from '@/services/billingService';
import { CacheService } from '@/services/cacheService';
import { supabase } from '@/config/supabase';

/**
 * Hook to manage user subscription status with real-time updates
 * Automatically invalidates cache when subscription changes
 * 
 * @param userIdOrEmail Supabase UUID (preferred) or email (fallback)
 */
export function useSubscription(userIdOrEmail?: string | null) {
  const [subscription, setSubscription] = useState<UserSubscription>({
    isPro: false,
    proExpiresAt: null,
    roadmapCount: 0,
    hasPendingRequest: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userIdOrEmail) {
      refreshSubscription();

      // Determine if input is UUID or email for proper filtering
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userIdOrEmail);

      // Subscribe to real-time updates for this user's profile
      const profileFilter = isUUID 
        ? `auth_user_id=eq.${userIdOrEmail}`
        : `email=eq.${userIdOrEmail}`;

      const channel = supabase
        .channel(`user-profile-${userIdOrEmail}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_profiles',
            filter: profileFilter
          },
          () => {
            console.log('🔄 Real-time update: user profile changed, refreshing subscription...');
            // Invalidate cache to ensure fresh data
            CacheService.invalidateCategory('subscription');
            refreshSubscription();
          }
        )
        .subscribe();

      // Also listen for changes in subscription_requests to update the 'Pending' status
      const requestChannel = supabase
        .channel(`subscription-requests-${userIdOrEmail}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'subscription_requests',
            filter: `user_email=eq.${userIdOrEmail}`
          },
          (payload) => {
            console.log('🔄 Real-time update: subscription request changed', payload);
            // Invalidate cache to show updated status immediately
            CacheService.invalidateCategory('subscription');
            refreshSubscription();
          }
        )
        .subscribe();

      // 🔄 Polling fallback: Check every 10 seconds in case real-time fails
      // This ensures users see updates even if WebSocket is down
      const pollInterval = setInterval(() => {
        console.log('⏱️ Polling subscription status (real-time fallback)...');
        CacheService.invalidateCategory('subscription');
        refreshSubscription();
      }, 10000); // 10 seconds

      return () => {
        supabase.removeChannel(channel);
        supabase.removeChannel(requestChannel);
        clearInterval(pollInterval);
      };
    }
  }, [userIdOrEmail]);

  const refreshSubscription = async () => {
    if (!userIdOrEmail) return;
    setLoading(true);
    const status = await BillingService.getSubscriptionStatus(userIdOrEmail);
    setSubscription(status);
    setLoading(false);
  };

  return { subscription, loading, refreshSubscription };
}
